import { ApolloLink, fromPromise, type ServerError } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import type { GraphQLFormattedError } from 'graphql'
import { toast } from 'sonner'
import { refreshSessionIfNeeded } from '@/shared/lib/nhost'

/**
 * Hasura meldet ein abgelaufenes Token auf drei Arten, je nachdem wo es auffällt:
 * als `invalid-jwt` in den Extensions, als Text im Fehler, oder als HTTP 401.
 */
export function isJwtExpiredError(error: GraphQLFormattedError): boolean {
  if (error.extensions?.code === 'invalid-jwt') return true
  return (
    error.message.includes('JWTExpired') ||
    error.message.includes('Could not verify JWT') ||
    error.message.includes('invalid-jwt')
  )
}

/** Verhindert, dass zehn gleichzeitig laufende Abfragen zehn Token-Erneuerungen auslösen. */
let pendingRefresh: Promise<boolean> | null = null

function refreshOnce(): Promise<boolean> {
  if (!pendingRefresh) {
    pendingRefresh = refreshSessionIfNeeded().finally(() => {
      pendingRefresh = null
    })
  }
  return pendingRefresh
}

function notifySessionExpired() {
  toast.error('Sitzung abgelaufen', {
    description: 'Bitte melde dich erneut an.',
    action: { label: 'Neu laden', onClick: () => window.location.reload() },
  })
}

/**
 * Erneuert das Token und schickt die gescheiterte Operation noch einmal los.
 *
 * Vorher hing das an einem `unhandledrejection`-Lauscher, der Fehlertexte verglich:
 * Der sah nur Fehler, die niemand abgefangen hatte, und konnte die Operation nicht
 * wiederholen - die Seite blieb leer, bis jemand neu lud. Im Link sieht Apollo jeden
 * Fehler, und `forward(operation)` wiederholt genau die eine Anfrage.
 *
 * `jwtRetried` im Kontext begrenzt das auf einen Versuch je Operation. Ohne die Marke
 * liefe ein dauerhaft ungültiges Token in einer Endlosschleife aus Erneuern und
 * Wiederholen.
 */
export const authRefreshLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const expired =
    graphQLErrors?.some(isJwtExpiredError) ||
    (networkError as ServerError | undefined)?.statusCode === 401
  if (!expired) return

  if (operation.getContext().jwtRetried) {
    notifySessionExpired()
    return
  }
  operation.setContext({ jwtRetried: true })

  return fromPromise(refreshOnce()).flatMap((refreshed) => {
    if (!refreshed) notifySessionExpired()
    // Auch nach einer gescheiterten Erneuerung weiterreichen: Dann kommt derselbe
    // Fehler zurück, aber als normaler Fehler der Abfrage - und nicht noch einmal hier.
    return forward(operation)
  })
})

/** Alles, was vor dem eigentlichen Netzwerk-Link hängt. */
export const apolloLink = ApolloLink.from([authRefreshLink])
